import ClockInAndOut from '../../../../Clock';

export const Core_SOP = {
    clockInOut: {
      title: "Clock In/Out Procedures",
      image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/clockInOutTitle.png",
      body: `
        <section>
          <h3>⏱️ Precision Builds Trust</h3>
          <p>
            Clocking in and out isn’t just about tracking hours — it reflects responsibility, reliability, and operational flow. <span class="highlight">This SOP ensures every shift starts and ends with clarity and accountability.</span>
          </p>
        </section>
    
        <section>
          <h4>🟢 1. Clocking In</h4>
          <ul>
            <li>📲 Upon arriving, immediately clock in using the Pawsible dashboard</li>
            <li>📍 Verify your location is correct, especially if covering multiple stores</li>
            <li>📝 Optional: Leave a shift note if you’re starting with a specific task or zone (e.g., “Opening Grooming Suite”)</li>
            <li>⏰ <span class="highlight-green">Clocking in must happen before beginning any pet handling, setup, or cleaning</span></li>
          </ul>
        </section>
    
        <section>
          <h4>🔴 2. Clocking Out</h4>
          <ul>
            <li>✅ Complete all duties and closing tasks before clocking out</li>
            <li>📝 Add relevant notes such as grooming progress, unusual behaviors, or delays</li>
            <li>⛔ <span class="highlight-red">Never leave shift without clocking out</span> — this affects payroll, logs, and team communication</li>
            <li>💬 If clocking out late due to extended service, note the reason (e.g., “Client pickup delay”)</li>
          </ul>
        </section>
    
        <section>
          <h4>📆 3. Viewing Shift Calendar</h4>
          <ul>
            <li>📅 The dashboard calendar shows past and upcoming shifts</li>
            <li>🗓️ Click on any date to view clock-in/out history, notes, and hours</li>
            <li>📣 <span class="highlight-blue">Use this feature to monitor your attendance and time accuracy</span></li>
          </ul>
        </section>
    
        <section>
          <h4>🛠️ 4. Fixing Shift Errors</h4>
          <ul>
            <li>🧾 If you forget to clock in/out, <span class="highlight-red">immediately report it to manager</span></li>
            <li>🔧 Include the correct time range and a brief note explaining the correction</li>
            <li>📣 Consistent errors may require a 1:1 check-in or shift shadowing</li>
          </ul>
        </section>
    
        <section>
          <h4>🚦 5. Clock In/Out Do's & Don'ts</h4>
          <ul>
            <li>✅ Do clock in on time, before starting any work</li>
            <li>✅ Do clock out after all assigned duties are complete</li>
            <li>🚫 Don’t clock in for someone else — this is a policy violation</li>
            <li>🚫 Don’t delay clocking out once your shift is fully wrapped</li>
          </ul>
          <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/clockInOutBody.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          <span class="highlight-green">Timeliness and transparency are the heartbeat of a well-run team.</span>
        </section>
    
        <section>
          <h4>📋 Quick-Glance Clock In/Out Checklist</h4>
          <ul>
            <li>✅ Clocked in before working</li>
            <li>✅ Verified location & notes if needed</li>
            <li>✅ Clocked out after completing shift duties</li>
            <li>✅ Reviewed shift calendar weekly for accuracy</li>
            <li>✅ Reported any time errors or tech issues</li>
          </ul>
        </section>
    
        <section>
          <h4>🌟 Final Thought</h4>
          <p><span class="highlight">Clocking in isn't just a punch — it’s a practice of showing up, clearly and proudly.</span> Stay sharp, stay honest, and keep the system strong for everyone on the team.</p>
        </section>
      `
    },  
    openingProcedures: {
        title: "Opening Procedures",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/openingProceduresTitle.png",
        body: `
          <section>
            <h3>🌞 Start the Day With Calm & Clarity</h3>
            <p>
              Opening isn’t just about unlocking doors — it’s about preparing an emotionally and physically safe environment for pets, staff, and parents. <span class="highlight">This SOP sets the tone for operational excellence, safety, and a standout client experience.</span>
            </p>
          </section>
      
          <section>
            <h4>🧭 1. Upon Arrival</h4>
            <ul>
              <li>🔑 Arrive 20 minutes before the opening hour</li>
              <li>🔓 Unlock designated entries and disarm security systems</li>
              <li>💡 Turn on lights and music (approved calming playlist)</li>
              <li>⏰ Cloak in for yourself</li>
            </ul>
          </section>
      
          <section>
            <h4>🧹 2. Full Facility Walkthrough</h4>
            <ul>
                <li>🧼 Check all public zones for cleanliness, odor, and safety hazards</li>
                <li>📦 Restock all consumables: poop bags, gloves, wipes, water bowls, toilet paper, soaps, etc.</li>
                <li>🧯 Inspect facility for safety and clutter-free walkways</li>
                <li>🛍️ <span class="highlight-red">By the opening hour, ensure front-of-house is fully presentable and ready to welcome clients</span></li>
            </ul>
            <span class="highlight-blue">This walkthrough verifies we’re operationally ready — safe, clean, and fully stocked for a smooth start.</span><br/>
          </section>
      
          <section>
            <h4>📋 3. Team Chat & Shift Board Review</h4>
            <ul>
              <li>📲 Check logs for any client updates, pet notes, or staff changes</li>
              <li>🗂️ Review appointments, boarding check-outs, meds, and behavior flags</li>
              <li>👩‍💼 Leaders: Assign zones and responsibilities</li>
            </ul>
          </section>
      
          <section>
            <h4>🏷️ 4. Pet Arrival Prep</h4>
            <ul>
              <li>📋 Prepare check-ins with client notes</li>
              <li>🏷️ Set up labeled crates/cubbies, water bowls, and treats for early birds</li>
              <li>🧠 Review known anxieties or medical needs — set up decompression zones as needed</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/openingProceduresWelcome.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>💬 5. Client-Ready Environment</h4>
            <ul>
                <li>🎉 Place signage where needed</li>
                <li>🧼 Sanitize reception, doors, desk surfaces, pens, and retail touchpoints</li>
                <li>🌼 Set up lobby: calming scents, water bowls, and optional music</li>
                <li>📠 Confirm any scheduled tours — prep welcome packets and staff assignments</li>
            </ul>
            <p><span class="highlight-green">Your setup communicates care before a single word is spoken.</span></p>
          </section>
      
            <section>
            <h4>📋 Quick-Glance Opening Checklist</h4>
            <ul>
                <li>✅ Facility walkthrough complete</li>
                <li>✅ All zones prepped & stocked</li>
                <li>✅ Reception ready for clients</li>
                <li>✅ Team Chat and schedule reviewed</li>
                <li>✅ Emergency stations and signage checked</li>
            </ul>
            </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight">Opening isn’t background work — it’s brand-building in action.</span> Create calm, set clear roles, and lead the day with thoughtful energy. Clients, pets, and your team feel the difference.</p>
          </section>
        `
      },
      closingProcedures: {
        title: "Closing Procedures",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/closingProceduresTitle.png",
        body: `
          <section>
            <h3>🌙 End the Day With Intention & Integrity</h3>
            <p>
              Closing the shop is not just about shutting doors — it’s about <span class="highlight">resetting the space with care, safety, and readiness for tomorrow</span>.
            </p>
          </section>
      
            <section>
                <h4>🧼 1. Facility Cleanup & Reset (Pre-Close)</h4>
                <p>
                    Around <strong>6PM</strong>, the team begins cleaning while dogs are still on-site. This helps us stay ahead and finish strong together — not leave it all to the closer. <span class="highlight">Clean space, calm pets, smooth close.</span>
                </p>
                <ul>
                    <li>🔒 Secure all daycare dogs to designated zones</li>
                    <li>🚿 Water down the faux grass using the foaming disinfectant sprayer</li>
                    <li>🧽 Scrub with the scrape broom to remove debris and waste</li>
                    <li>🌬️ Roll up the artificial turf to dry overnight</li>
                    <li>🕳️ Drain all standing water fully</li>
                    <li>🧰 Empty and rinse the turf drain filter basket</li>
                    <li>💨 Turn on fans & AC to dry the floor for ventilation</li>
                    <li>🧹 Sweep and mop lobby & bathroom floors</li>
                    <li>🧼 Sanitize crates, water bowls, and food stations not in active use</li>
                    <li>🧺 Run a final load of laundry if baskets are full — rotate clean towels</li>
                    <li>🧴 Restock items in all areas</li>
                </ul>
                <div class="embedded-visual">
                  <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/closingProceduresCleaning.png" alt="Real-Life Incident Example" class="inline-guide-image" />
                </div>
                <p><span class="highlight-blue">This shared routine ensures we close as a team — not as individuals cleaning in the dark.</span></p>
            </section>
      
          <section>
            <h4>📋 2. Pet & Parent Final Checks</h4>
            <ul>
              <li>📦 Double-check cubbies for belongings: leashes, meds, notes - no leftovers</li>
              <li>📝 Confirm all pets are picked up or secured in proper boarding spaces</li>
              <li>📩 Leave any late pick-up notes or parent messages in team chat</li>
            </ul>
          </section>
      
          <section>
            <h4>💬 3. Client Communication Wrap-Up</h4>
            <ul>
              <li>📸 Upload any final media contents to team chat</li>
              <li>📲 Flag unresolved client concerns to next day’s team</li>
            </ul>
          </section>
      
          <section>
            <h4>🧭 4. Safety & Security</h4>
            <ul>
              <li>🚪 Lock all exterior doors and secure gate latches</li>
              <li>🔌 Turn off unnecessary electronics, check appliance timers, and secure cords</li>
              <li>💡 Shut down interior lights</li>
              <li>🔒 Set alarm system before exiting</li>
            </ul>
            <p><span class="highlight-red">Safety is non-negotiable — never skip a lock, latch, or alarm.</span></p>
          </section>
      
          <section>
            <h4>📲 5. Team Chat & Dashboard Check-Out</h4>
            <ul>
              <li>🧠 Log final notes in #daily-shift-log (include: notable pets, incidents, supply alerts)</li>
              <li>🧾 Mark any pending tasks or handoffs for morning crew</li>
              <li>⏱️ Clock out via the staff dashboard</li>
            </ul>
          </section>
      
          <section>
            <h4>✅ End-of-Day Checklist</h4>
            <ul>
              <li>✅ All pets secured or picked up</li>
              <li>✅ Zones cleaned and sanitized</li>
              <li>✅ Client notes and shifts logged</li>
              <li>✅ Security protocols completed</li>
              <li>✅ Clock out complete</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">A clean close creates a strong open.</span> Treat end-of-day like a reset — not a rush. Your care carries through tomorrow’s first tail wag.</p>
          </section>
        `
      },
      emergencyProtocols: {
        title: "Emergency Protocols",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/emergencySOPTitle.png",
        body: `
          <section>
            <h3>🚨 When the Unexpected Happens</h3>
            <p>
              Emergencies can’t always be predicted — but <span class="highlight">they can be prepared for</span>. Whether it’s a pet injury, a power outage, or a natural disaster, this SOP ensures that every team member knows what to do, who to alert, and how to respond calmly and professionally.
            </p>
          </section>

        <section>
            <h4>🧠 1. Staff Injury or Mental Health Emergency</h4>
            <ul>
                <li>🩹 <strong>For Physical Injury:</strong> Stop all tasks. Support the team member to a safe location and apply first aid if appropriate</li>
                <li>📞 <strong>Notify lead immediately</strong> — even for minor injuries like cuts, slips, or bites</li>
                <li>🧾 <strong>Document the incident</strong> in the injury log and complete any required reports</li>
                <li>🧘 <strong>For Emotional or Mental Health:</strong> Offer space and grounding support — “Want to step outside or take 5?”</li>
                <li>🤝 <strong>Reassign tasks</strong> if someone appears panicked, overwhelmed, or shaken by an incident</li>
                <li>📣 <strong>Call for additional support</strong> (another lead, manager, or emergency contact) if needed</li>
            </ul>
            <p><span class="highlight-yellow">Our staff are not just workers — they are caretakers, teammates, and humans. Protecting them is part of our core safety culture.</span></p>
        </section>
      
          <section>
            <h4>💥 2. Pet Fight or Injury</h4>
            <ul>
              <li>📢 Loud clap or firm voice to interrupt</li>
              <li>🔒 Separate safely and immediately secure both animals</li>
              <li>🩹 First aid administered as needed; note any swelling, limping, or wounds</li>
              <li>📋 Log incident in dashboard; tag as “urgent” and notify lead on duty</li>
              <li>📸 Take photo documentation (injury, behavior, surroundings)</li>
              <li>📞 Leaders: Call pet parent promptly with a calm, clear report</li>
            </ul>
            <p><span class="highlight-red">Pet safety is our top priority — always escalate injuries to leads, no matter how minor they seem.</span></p>
          </section>
      
          <section>
            <h4>⚡ 3. Power Outage or Facility Malfunction</h4>
            <ul>
              <li>💡 Check fuse box and backup systems first</li>
              <li>📞 Notify manager and team chat “#urgent” if water/power/AC is lost</li>
              <li>🐶 Secure pets to designated zones when needed</li>
            </ul>
          </section>
      
          <section>
            <h4>🚪 4. Missing Pet or Escape Attempt</h4>
            <ul>
              <li>🚨 Sound internal alert — all staff freeze, doors are closed immediately</li>
              <li>📍 Last seen area is secured and retraced with 2+ staff</li>
              <li>📞 Manager contacted and search begins within the facility perimeter</li>
              <li>📸 Leaders: Contact parent if not found in 10 minutes</li>
              <li>🧾 Log incident + debrief team after resolution</li>
            </ul>
          </section>
      
         <section>
            <h4>🔥 5. Fire, Earthquake, or Evacuation</h4>
            <ul>
              <li>🚪 Begin evacuation immediately — no personal items</li>
              <li>🧾 Take emergency procedures (go over pet roster, medical injuries, parent contacts)</li>
              <li>📍 Lead pets to designated outdoor emergency area; use leashes</li>
              <li>📞 Notify leadership and emergency services</li>
              <li>🫂 Reassure pets and teammates and confirm accountability</li>
            </ul>
              <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/emergencySOPAccountability.png" alt="Real-Life Incident Example" class="inline-guide-image" />
              </div>
          </section>
      
          <section>
            <h4>📋 Final Note</h4>
            <p><span class="highlight-green">Every emergency is a chance to protect, lead, and strengthen trust</span>. Know your steps, lean on your team, and always act fast but with calm care.</p>
          </section>
        `
      },
      incidentReporting: {
        title: "Incident Reporting",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/incidentReportingTitle.png",
        body: `
          <section>
            <h3>📋 Why Incident Reporting Matters</h3>
            <p>
              Incidents aren’t just moments — they’re data points that help us <span class="highlight">protect pets, improve protocols, and build trust</span>. Whether it’s a fight, injury, equipment issue, or client concern, timely and clear reporting ensures nothing gets missed.
            </p>
          </section>
      
          <section>
            <h4>🛠️ When to Log an Incident</h4>
            <ul>
              <li>🐾 Pet injury (even minor scrapes or limps)</li>
              <li>💥 Dog fight or over-arousal during play</li>
              <li>💊 Medication error or unexpected reaction</li>
              <li>💬 Escalated client interaction</li>
              <li>🔧 Facility malfunction (e.g., broken crate, power loss, gate latch failure)</li>
              <li>🧍‍♂️ Staff injury or behavior breach</li>
            </ul>
            <p><span class="highlight-blue">If you’re unsure — log it. Better to over-report than miss a pattern.</span></p>
          </section>
      
          <section>
            <h4>🖥️ Where to Report</h4>
            <ul>
              <li>🗂️ Go to the <strong>Incident Log</strong> under the “Tasks” tab in your dashboard</li>
              <li>📝 Fill out the form with date, time, pet(s), staff, zone, and a neutral account of the event</li>
              <li>📸 Attach any relevant images or video (optional but helpful)</li>
            </ul>
            <p><span class="highlight">This is not a venting space — it’s a professional tool for clarity, safety, and accountability.</span></p>
          </section>
      
          <section>
            <h4>🧠 How to Write It</h4>
            <ul>
              <li>✅ Stick to facts: who, what, when, where</li>
              <li>🧭 Avoid opinions - focus on observation</li>
              <li>🔄 Include follow-up taken (e.g., moved dogs apart, informed client, cleaned wound)</li>
              <li>📨 Notify on team chat after logging</li>
            </ul>
            <p><span class="highlight-green">Clean documentation supports the whole team — and protects everyone involved.</span></p>
              <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/incidentReportingTeam.png" alt="Real-Life Incident Example" class="inline-guide-image" />
              </div>
          </section>
      
          <section>
            <h4>📣 Final Thought</h4>
            <p><span class="highlight">Clear, neutral incident logs build safer practices and more trust with clients.</span> Take 3 minutes to write it up — it’s part of what makes Pawsible elite.</p>
          </section>
        `
      },
      sanitationStandards: {
        title: "Sanitation Standards",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/sanitationStandardsTitle.png",
        body: `
          <section>
            <h3>🧼 Clean Isn’t Optional — It’s Operational</h3>
            <p>
              Cleanliness is safety. It protects pets, staff, and clients. This SOP defines <span class="highlight">when, where, and how sanitation must be handled</span> to meet our standards and support wellbeing across all Pawsible zones.
            </p>
          </section>
      
          <section>
            <h4>🧽 1. Core Cleaning Zones</h4>
            <ul>
              <li>🐾 <strong>Daycare & Play Areas:</strong> Spot clean throughout day; full sanitation at closing</li>
              <li>🛁 <strong>Grooming Zones:</strong> Sanitize table, tools, and tubs between every pet</li>
              <li>🛏️ <strong>Boarding/Quiet Rooms:</strong> Clean crates after each stay and as needed during visits</li>
              <li>🧴 <strong>Reception & Touchpoints:</strong> Sanitize desk, pens, card readers, and doorknobs daily</li>
            </ul>
          </section>
      
          <section>
            <h4>🧴 2. Daily Disinfection Checklist</h4>
            <ul>
              <li>✅ Mop floors with pet-safe disinfectant</li>
              <li>✅ Spray & wipe down shared equipment: crates, feeding areas, and water bowls</li>
              <li>✅ Empty trash and replace liners — especially in zones with organic waste</li>
              <li>✅ Clean and refill hand sanitizers, gloves, and paper towels</li>
            </ul>
          </section>
      
          <section>
            <h4>🧺 3. Laundry Protocol</h4>
            <ul>
              <li>📥 Dirty towels go in designated bins — never on floors or mixed with clean</li>
              <li>🌀 Wash with unscented detergent on HOT setting</li>
              <li>🚫 No fabric softeners (can cause irritation for pets)</li>
              <li>🧺 Fold and restock to grooming, boarding, or first aid zones</li>
            </ul>
          </section>
      
          <section>
            <h4>🧯 4. Product Safety & Storage</h4>
            <ul>
              <li>🧴 Only use approved, labeled cleaning agents — never mix chemicals</li>
              <li>🗂️ <span class="highlight-red"> Store products in closed bins out of pet reach</span></li>
              <li>📦 Refill from supply closet only — don’t leave bottles in random areas</li>
            </ul>
          </section>
      
          <section>
            <h4>🔁 5. Clean-As-You-Go Culture</h4>
            <ul>
              <li>🧼 Clean the zone before leaving it</li>
              <li>📣 Report any messes you can't clean immediately (log it + notify lead)</li>
              <li>🤝 Encourage teammates to help maintain order without shaming or blaming</li>
            </ul>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/sanitationStandardsAsWeGo.png" alt="Real-Life Incident Example" class="inline-guide-image" />
              </div>
            <p><span class="highlight-blue">Sanitation is everyone’s responsibility — and a direct reflection of our standards.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Clean is kind, safe, and professional.</span> Whether clients see it or not — the effort always shows in the pets’ wellbeing and the team’s flow.</p>
          </section>
        `
      },
      communicationProtocol: {
        title: "Communication Protocol",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/communicationProtocolTitle.png",
        body: `
          <section>
            <h3>📣 Clear, Calm, and Connected</h3>
            <p>
              Great communication keeps pets safe, shifts smooth, and teams aligned. 
              This SOP outlines how we <span class="highlight">connect quickly, respectfully, and effectively</span> using walkie-talkies and team chat.
            </p>
          </section>
      
          <section>
            <h4>📻 1. Walkie-Talkie Use (On-Site)</h4>
            <ul>
              <li>🔋 Pick up your walkie at clock-in and ensure battery level is good</li>
              <li>🎤 Press to speak, release to listen — no overlapping chatter</li>
              <li>🗣️ Keep messages short and calm: “Max needs an escort to grooming, please” or “Need backup in Daycare”</li>
              <li>🚫 No joking, gossip, or side convos — walkies are for operations only</li>
              <li>🔕 Use code words sparingly and only when trained (e.g. "Dog Out” for escape of dog)</li>
            </ul>
            <p><span class="highlight-yellow">Walkies-talkies are lifelines. Use them to smooth out the operations.</span></p>
          </section>
      
          <section>
            <h4>💬 2. Team Chat: Digital Communication</h4>
            <ul>
              <li>📲 <strong>#daily-shift-log:</strong> Daily priorities, pet notes, schedule changes</li>
              <li>🚨 <strong>#incident-reports:</strong> Logged ASAP with objective language and photos if needed</li>
              <li>🎉 <strong>#general-updates:</strong> Happy moments, kudos, reminders, birthday flags, and wins</li>
              <li>🧠 <strong>#help-desk:</strong> Use for questions, feedback, or policy clarifications</li>
            </ul>
            <p><span class="highlight-blue">Team chat is our paper trail — if it’s important, log it.</span> Huddles cover the moment; team chat captures the memory.</p>
          </section>
      
          <section>
            <h4>🤝 3. Tone, Timing, and Trust</h4>
            <ul>
              <li>🧠 Be clear, not clipped — “I need help” > “Can someone…?”</li>
              <li>👀 Speak to teammates like you're in the same room (tone matters!)</li>
              <li>📍 Use names when possible — “Hey Jordan, can you cover daycare yard A?”</li>
              <li>🤫 Keep urgent channel use focused — update casually in person or in #general</li>
            </ul>
              <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/communicationProtocolCommunication.png" alt="Real-Life Incident Example" class="inline-guide-image" />
              </div>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">When in doubt, speak it out.</span> A missed message can lead to a safety risk or misstep. Use the tools, use them often, and use them well.</p>
          </section>
        `
      },
      clientInteraction: {
        title: "Client Interaction SOP",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/clientInteractionTitle.png",
        body: `
          <section>
            <h3>💬 Every Interaction Builds the Brand</h3>
            <p>
              Whether it’s a 10-second hello or a 10-minute tour, <span class="highlight">how we communicate with pet parents shapes trust, retention, and referrals.</span> This SOP ensures every team member handles interactions with consistency, care, and clarity.
            </p>
          </section>
      
          <section>
            <h4>👋 1. Greeting Clients On Arrival</h4>
            <ul>
              <li>🫶 Greet every client by name when possible — and say the pet’s name too</li>
              <li>📋 Confirm check-in with a warm tone: “We’re so happy to see Max today!”</li>
              <li>👀 Observe body language — if they seem rushed or concerned, slow down and offer support</li>
              <li>🤫 Speak calmly around anxious pets (no squealing or loud greetings)</li>
            </ul>
            <p><span class="highlight-yellow">First impressions happen every visit — not just the first time.</span></p>
          </section>
      
          <section>
            <h4>📣 2. Communicating Updates & Incidents</h4>
            <ul>
              <li>🧠 <strong>Be proactive:</strong> Share relevant notes without waiting to be asked</li>
              <li>💛 <strong>Use kind, clear language:</strong> “Juno was a little nervous today — we gave her extra space and she settled nicely”</li>
              <li>📸 <strong>Offer photos:</strong> For new pups, milestones, or enrichment activities</li>
              <li>📝 Log everything in the team chat first — verbal updates are helpful but not enough</li>
            </ul>
          </section>
      
          <section>
            <h4>🧭 3. Handling Questions or Concerns</h4>
            <ul>
              <li>👂 <strong>Listen first:</strong> Let them speak fully before responding</li>
              <li>📘 <strong>Validate, then clarify:</strong> “Totally fair question — here’s how we usually approach it…”</li>
              <li>🧍 <strong>Never argue:</strong> Escalate to a lead if emotions rise or policy pushback happens</li>
              <li>📩 <strong>Follow up:</strong> If you say “We’ll check and let you know,” be sure to do it by EOD</li>
            </ul>
          </section>
      
          <section>
            <h4>🎁 4. Checkout & Goodbye</h4>
            <ul>
              <li>📦 Share a mini update: “Charlie made new friends in yard A today!”</li>
              <li>🖐️ Offer a calm and cheerful goodbye — “See you Wednesday!” or “We’ll miss Bear’s wiggles!”</li>
              <li>🧼 Ensure pet is clean, brushed, and leashed safely before hand-off</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/clientInteractionImage.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-green">Final moments leave a lasting emotional imprint — always end on a high note.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight">Pet parents are entrusting us with family.</span> Treat every interaction with kindness, clarity, and calm professionalism. That’s what makes Pawsible more than a pet business — it makes us trusted partners in their pet’s journey.</p>
          </section>
        `
      },
      uniformStandards: {
        title: "Uniform & Hygiene Standards",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/uniformStandardsTitle.png",
        body: `
          <section>
            <h3>🧼 Your Presence = Their Confidence</h3>
            <p>
              Clean, consistent appearance shows clients and teammates that we take care seriously. <span class="highlight">Our uniforms and hygiene protocols help create a sense of calm, professionalism, and trust for pets and parents alike.</span>
            </p>
          </section>
      
          <section>
            <h4>👕 1. Uniform Requirements</h4>
            <ul>
              <li>👚 <strong>Shirts:</strong> Light navy Pawsible-branded shirt must be worn during all on-site shifts</li>
              <li>👖 <strong>Bottoms:</strong> Black or neutral pants, joggers, or knee-length shorts — no rips, holes, or excessive logos</li>
              <li>👟 <strong>Shoes:</strong> Closed-toe, slip-resistant footwear (no sandals or Crocs)</li>
              <li>🧢 <strong>Optional:</strong> Masks or aprons may be worn in grooming zones or during the cleaning</li>
            </ul>
          </section>
      
          <section>
            <h4>🫧 2. Daily Hygiene Expectations</h4>
            <ul>
              <li>🧼 Arrive in a clean uniform — no visibly dirty clothing</li>
              <li>🧴 Wash hands or sanitize before/after:
                <ul>
                  <li>🐶 Handling pets or crates</li>
                  <li>🍴 Preparing or touching food</li>
                  <li>🧻 Using restroom or cleaning up waste</li>
                </ul>
              </li>
              <li>💦 Deodorant, clean fingernails, and tied-back hair (if long) are required</li>
              <li>❌ No heavy perfumes or sprays — sensitive pet noses!</li>
            </ul>
          </section>
      
          <section>
            <h4>🎒 3. What to Bring Daily</h4>
            <ul>
              <li>👕 At least one backup shirt & socks (accidents happen)</li>
              <li>🌧️ Weather-appropriate layers if working outdoor zones</li>
              <li>🎓 Phone with access to SOP & dashboard (no personal use during shifts)</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/uniformStandardsTeam.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>📏 4. Professionalism Notes</h4>
            <ul>
              <li>🧠 Dress code is part of the first impression — always wear full uniform unless instructed otherwise</li>
              <li>🚫 No earbuds, chewing gum, or mid-shift phone use unless cleared for operational needs</li>
              <li>🫶 Respect shared locker areas — keep your space tidy and clean out items weekly</li>
            </ul>
            <p><span class="highlight-yellow">Think of what you'd wear to represent a trusted pet care brand — that’s the energy we dress with every day.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Your uniform signals safety, consistency, and care.</span> Show up polished, clean, and ready — because how we show up is part of the service we provide.</p>
          </section>
        `
      },
      petIdSafety: {
        title: "Pet ID & Transport Safety",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/petIdSafetyTitle.png",
        body: `
          <section>
            <h3>🔖 Visibility & Movement = Safety</h3>
            <p>
              At Pawsible, identification and safe movement go hand in hand. <span class="highlight">Every pet must be clearly marked and transported with care — from lobby to grooming table, and play yard to crate.</span>
            </p>
          </section>
      
          <section>
            <h4>🟡 Collar Colors & Service Tags</h4>
            <p>Apply the correct collar color and tags at check-in based on their scheduled services:</p>
            <ul>
              <li>🟡 <strong>Yellow:</strong> Daycare</li>
              <li>🔴 <strong>Red:</strong> Allergies or Medication
              <li>🟣 <strong>Purple:</strong> Grooming</li>
              <li>🟠 <strong>Orange:</strong> Boarding</li>
              <li>⚫ <strong>Black:</strong> Training</li>
            </ul>
            <p><span class="highlight-blue">Collars must always be visible and secure before entering any zone.</span> Don’t mix collars. If unclear, ask a lead or manager immediately.</p>
          </section>
      
          <section>
            <h4>🚶‍♂️ Pet Transport Protocol</h4>
            <ul>
              <li>🔗 Always clip leash securely — even for short moves across zones</li>
              <li>🐱 <span class="highlight-red">Always carry cats in container</span> as it can run off or get in trouble with dogs</li>
              <li>🚪 Only transport one pet at a time through entryways or narrow halls</li>
              <li>🧼 Use wheeled crates if available for pets under recovery or with mobility risks</li>
              <li>🔍 <span class="highlight-red">Never drag:</span> Pause if the pet seems anxious — redirect with a treat or verbal cue</li>
              <li>🛑 Avoid crossover or crowding during peak transition times</li>
            </ul>
            <div class="embedded-visual">
              <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/petIdSafetyTransport.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
            <p><span class="highlight-yellow">How we move pets shapes their emotional safety and our operational flow.</span></p>
          </section>
      
          <section>
            <h4>📋 Final Checks</h4>
            <ul>
              <li>✅ Confirm every collar and tag before transitions or handoffs</li>
              <li>📝 Check Tasks board for behavior or medical alerts before putting on collars</li>
              <li>📲 Verify ID tag before releasing to shuttle, parent, or groomer</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Pet movement isn’t background noise — it’s a frontline safety step.</span> Stay sharp, stay calm, and always check collars before doors open.</p>
          </section>
        `
      }     
  };
  